/**
 * Business Opportunities: every idea researched as a business opportunity,
 * shared by everyone, in two views over one set of
 * filters: Matrix (Impact × Feasibility, the default) and List (the scored table). The
 * view and the filters live in the URL (see ideaFilters.ts), so switching
 * views keeps the filters and a filtered view can be linked. The portfolio's
 * KPIs are on the Dashboard; this page keeps one line of counts.
 */

import { Link } from 'react-router-dom';
import { Grid3x3 } from 'lucide-react';

import type { Dimension, IdeaCatalogue, IdeaPoints } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';

import { IdeaTable } from './IdeaTable';
import { IdeasToolbar } from './IdeasToolbar';
import { OpportunityMatrix } from './OpportunityMatrix';
import { ScoringGuideDialog } from './ScoringGuideDialog';
import { useOpenSubmitIdea } from './SubmitIdeaDialog';
import type { ColorBy } from './ideaFilters';
import { axisDimensions, matrixPoints } from './matrixAxes';
import { useBusinessOpportunities } from './useBusinessOpportunities';

function MissingAxes() {
  const { can } = useSession();
  return (
    <StateCard icon={<Grid3x3 aria-hidden="true" />} title="The matrix needs both axes">
      <p>
        Every idea is placed by the dimensions that feed Impact and those that feed Feasibility, and one of the two has none.{' '}
        {can('research.dimensions.manage') ? (
          <Link to="/configuration?tab=business-opportunities&part=dimensions">Set the axes in Configuration → Business Opportunities.</Link>
        ) : (
          'An admin sets them in Configuration → Business Opportunities.'
        )}
      </p>
    </StateCard>
  );
}

function MatrixView({
  data,
  catalogue,
  dimensions,
  filtered,
  colorBy,
}: {
  data: IdeaPoints;
  catalogue: IdeaCatalogue;
  dimensions: Dimension[];
  filtered: boolean;
  colorBy: ColorBy;
}) {
  if (axisDimensions(dimensions, 'impact').length === 0 || axisDimensions(dimensions, 'feasibility').length === 0) {
    return <MissingAxes />;
  }
  const { points } = matrixPoints(data.points, dimensions);
  if (points.length === 0) {
    return (
      <div className="empty">
        <div className="e-icon"><Grid3x3 aria-hidden="true" /></div>
        <h3>{filtered ? 'No scored ideas match' : 'No scored ideas yet'}</h3>
        <p>{filtered ? 'Clear the search or the filters to see the rest.' : 'Ideas appear here once research has scored them.'}</p>
      </div>
    );
  }
  return (
    <OpportunityMatrix points={points} threshold={catalogue.matrix_threshold} colorBy={colorBy} industries={catalogue.industries} />
  );
}

const head = (
  <PageHead
    title="Business Opportunities"
    subtitle="Every idea, researched as a business opportunity and scored on weighted dimensions"
  />
);

export function BusinessOpportunitiesPage() {
  const openSubmitIdea = useOpenSubmitIdea();
  const page = useBusinessOpportunities();
  const { catalogue, ideas, filters, dimensions } = page;
  const { view, params, filtered, refine, setPage } = filters;

  if (catalogue.isPending || (ideas.isPending && !ideas.data)) return <>{head}<LoadingCard label="Loading ideas" /></>;
  if (catalogue.error) return <>{head}<LoadFailure error={catalogue.error} onRetry={() => void catalogue.refetch()} /></>;
  if (ideas.error && !ideas.data) return <>{head}<LoadFailure error={ideas.error} onRetry={() => void ideas.refetch()} /></>;

  const threshold = catalogue.data.matrix_threshold;

  return (
    <>
      {head}

      {/* Beyond six dimensions the columns fold behind the toggle at any width. */}
      <div className={`matrix-area${dimensions.length > 6 ? ' many-dims' : ''}`}>
        <div className="card matrix-card">
          <IdeasToolbar
            filters={filters}
            counts={page.counts}
            outcomeCounts={page.outcomeCounts}
            industries={catalogue.data.industries}
            showDimensions={page.showDimensions}
            setShowDimensions={page.setShowDimensions}
            onSubmitIdea={openSubmitIdea}
            onShowGuide={() => page.setShowGuide(true)}
          />
          {view === 'list' ? (
            <IdeaTable
              data={page.list.data!}
              params={params}
              dimensions={dimensions}
              filtered={filtered}
              showDimensions={page.showDimensions}
              refine={refine}
              setPage={setPage}
              onSubmitIdea={openSubmitIdea}
            />
          ) : (
            <MatrixView data={page.matrix.data!} catalogue={catalogue.data} dimensions={dimensions} filtered={filtered} colorBy={filters.colorBy} />
          )}
        </div>
      </div>

      {page.showGuide && <ScoringGuideDialog dimensions={dimensions} threshold={threshold} onClose={() => page.setShowGuide(false)} />}
    </>
  );
}
